/**
 * Custom-field types — Step 28/57. One list, shared by the builder (which
 * type of control to render), the renderer (edit + view), and validation.
 * Matching Freshservice's set is deliberate — that's the bar the nine real
 * Microsoft Forms (Step 57) measure the builder against.
 */
export const FIELD_TYPES = [
  'text', // single-line
  'textarea', // paragraph
  'richtext',
  'number',
  'decimal',
  'select', // single dropdown; supports allowOther and parent-driven cascading options
  'multiselect',
  'radio',
  'checkbox', // boolean
  'date',
  'datetime',
  'url',
  'email',
  'phone',
  'file',
  'person', // org-directory lookup — Step 57's addition, a field type not free text
  'text_list', // repeatable entries — Step 57's addition, instead of a comma-separated textarea
  'content', // static instructions; renders, takes no input, never validated or stored
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export interface SelectOption {
  value: string;
  label: string;
  /** Archived options never disappear from a ticket that already holds them
   *  — only from the pickers offered on new submissions (plan Risk: a
   *  deleted option orphans historical values). */
  archived?: boolean;
}

/** Generic per-definition config — which keys apply depends on `type`. */
export interface FieldConfig {
  placeholder?: string;
  /**
   * Step 70 — a default written as a token, e.g. `{{requester.department}}`,
   * resolved SERVER-SIDE at render from the directory attributes of whoever
   * the form is for.
   *
   * Placeholders only, never a free literal: a typed-in default is a value
   * nobody chose that arrives pre-agreed, and it reads as though the
   * requester answered it. `prefillError` is the rule; the builder and the
   * API both run it.
   */
  defaultToken?: string;
  options?: SelectOption[];
  allowOther?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  /** Shown instead of the generic message when `pattern` fails (Step 66). */
  patternMessage?: string;
  /** Cascading options (the plan's "dependent" type, folded into `select`
   *  rather than a separate type): options are looked up by the CURRENT
   *  value of an earlier field, e.g. Country → State. */
  cascade?: { dependsOnKey: string; optionsByParentValue: Record<string, SelectOption[]> };
  fileAccept?: string; // e.g. ".pdf,.png"
  maxFileSizeMb?: number;
  /** How many files this field accepts. 1 unless set; the reference caps at
   *  10 and so do we — a form asking for more is asking for a folder. */
  maxFiles?: number;
  dateOnly?: boolean; // datetime fields: true hides the time picker (help-text-driven per Step 57)
  /** `select`/`multiselect` only — when set, options come from matching the
   *  directory (job title, department, kind, group membership) instead of
   *  the hand-entered `options` list above. Same Condition grammar as
   *  `showWhen`, evaluated against a person's attributes rather than a
   *  form's field values (Kai: "populate the dropdowns with people's names
   *  who have X job title... or everyone in X department"). */
  peopleFilter?: import('./conditions.ts').Condition;
}

export interface FieldDefinition {
  id: string;
  key: string; // immutable machine key
  /**
   * Step 149 — one of the eight built-in ticket properties (status, priority,
   * urgency, impact, type, group, assignee, source), stored as a definition so
   * its LABEL and OPTIONS are the tenant's to edit while its KEY and TYPE are
   * not. The rail renders a system field with its own control (a status is a
   * status), and a custom one with the generic renderer.
   */
  isSystem?: boolean;
  label: string;
  helpText?: string;
  type: FieldType;
  config: FieldConfig;
  /** 0 = ARCHIVED (Step 67): gone from every picker and builder, but still
   *  rendered on the tickets that already hold an answer — deleting instead
   *  would blank those answers (plan risk #9). */
  isActive?: number;
  // Locks `type` once true — set the first time this field is attached to a
  // PUBLISHED layout (a field never used anywhere is still safe to reshape;
  // one already collecting answers is not — changing its type would make
  // past answers unreadable under the new type's rules).
  hasBeenUsed?: number;
}

/** One uploaded file as a field VALUE. Deliberately not the bytes: the
 *  upload happens first and returns an id, so a form submission carries
 *  references, and a half-filled form never holds megabytes of state. */
export interface FileFieldValue {
  id: string;
  filename: string;
  contentType: string;
  size: number;
}

/** A field's presence and behaviour ON ONE FORM — the same shared
 *  `FieldDefinition` can be required on one layout and optional on another,
 *  which is the whole point of a shared field library. */
export interface LayoutField {
  fieldId: string;
  required: boolean;
  /** Requester-visible (both) or internal-only (agent-only, e.g. "Cost
   *  centre approved?" on a service request nobody outside IT should see). */
  audience: 'both' | 'agent-only';
  /** Step 66's behaviour matrix, layered on `audience` rather than replacing
   *  it — sections are a JSON column, so an absent flag simply means the
   *  historic default (a requester-visible field was editable), which is why
   *  this needs no data migration. `false` renders the field read-only on
   *  every requester surface AND makes the server drop any value a client
   *  submits for it: visibility and writability are separate questions.
   *  `displayToApprover` is the reserved third axis; it lands with Step 74's
   *  approvals, and nothing reads it yet. */
  requesterCanEdit?: boolean;
  displayToApprover?: boolean;
  filterable: boolean;
  showWhen?: import('./conditions.ts').Condition;
}

/** A LayoutField joined with its FieldDefinition — what the renderer and
 *  the validator actually consume. Fetched pre-resolved from the server so
 *  client and server always agree on field ORDER (conditions may only
 *  reference an EARLIER field — a DAG by construction). */
export interface ResolvedField extends LayoutField {
  definition: FieldDefinition;
}
